// src/context/AuthContext.tsx

import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import type { AuthUser } from '../utils/auth';
import type { Session } from '@supabase/supabase-js';

interface AuthContextValue {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  /** False when authenticated but the email is not in the ALLOWED_EMAILS allowlist. */
  isAllowed: boolean;
  accessToken: string | null;
  login: (provider: 'google' | 'github' | 'discord') => Promise<void>;
  logout: () => Promise<void>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// Module-scoped for fetchWithCsrf access
let storedAccessToken: string | null = null;

function sessionToUser(session: Session): AuthUser {
  const meta = session.user.user_metadata;
  return {
    id: session.user.id,
    email: session.user.email ?? '',
    name: meta?.full_name ?? meta?.name ?? session.user.email ?? '',
    groups: (session.user.app_metadata?.groups as string[]) ?? [],
  };
}

async function fetchMe(): Promise<{ isAdmin: boolean; allowed: boolean }> {
  try {
    const res = await fetch('/api/me', {
      headers: storedAccessToken ? { Authorization: `Bearer ${storedAccessToken}` } : undefined,
      credentials: 'include',
    });
    if (!res.ok) return { isAdmin: false, allowed: false };
    const data = await res.json();
    return {
      isAdmin: Boolean(data?.isAdmin),
      // Default to allowed when the server doesn't include the flag (e.g. older
      // build before Wave 4) so we don't accidentally lock the owner out.
      allowed: data?.allowed === undefined ? true : Boolean(data.allowed),
    };
  } catch {
    return { isAdmin: false, allowed: false };
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isAllowed, setIsAllowed] = useState(true);
  const [loading, setLoading] = useState(true);

  const isAuthenticated = user !== null;

  const handleSession = useCallback(async (session: Session | null, event?: string) => {
    if (session) {
      storedAccessToken = session.access_token;
      // TOKEN_REFRESHED fires in EVERY tab on each auto-refresh. Identity and
      // admin/allowed status don't change on a refresh, so just update the
      // token (fetchWithCsrf reads it) and skip the /api/me call + re-render —
      // otherwise every refresh triggers a fetchMe herd across all open tabs.
      if (event === 'TOKEN_REFRESHED') return;
      setUser(sessionToUser(session));
      const me = await fetchMe();
      setIsAdmin(me.isAdmin);
      setIsAllowed(me.allowed);
    } else {
      storedAccessToken = null;
      setUser(null);
      setIsAdmin(false);
      setIsAllowed(true);
    }
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      await handleSession(session);
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        void handleSession(session, event);
      }
    );

    return () => subscription.unsubscribe();
  }, [handleSession]);

  const login = useCallback(async (provider: 'google' | 'github' | 'discord') => {
    await supabase.auth.signInWithOAuth({
      provider,
      options: { redirectTo: `${window.location.origin}/callback` },
    });
  }, []);

  const logout = useCallback(async () => {
    await supabase.auth.signOut();
    storedAccessToken = null;
    setUser(null);
    setIsAdmin(false);
    setIsAllowed(true);
    window.location.href = '/';
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, isAuthenticated, isAdmin, isAllowed, accessToken: storedAccessToken, login, logout, loading }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}

export function getAccessToken(): string | null {
  return storedAccessToken;
}

export function setStoredAccessToken(token: string): void {
  storedAccessToken = token;
}
